import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Badge, Table, Spinner, Alert } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import './Produits.css';

export default function DetailProduit() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [produit, setProduit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mouvements, setMouvements] = useState([]);

    useEffect(() => {
        fetchProduit();
        fetchMouvements();
    }, [id]);

    const fetchProduit = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/produits.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setProduit(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchMouvements = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/mouvements_stock.php?produit=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (response.ok) {
                const result = await response.json();
                setMouvements(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement mouvements", err);
        }
    };

    const getStockStatus = () => {
        if (!produit) return { label: '', color: '' };
        if (!produit.stock_actuel || produit.stock_actuel <= 0) {
            return { label: 'Rupture', color: 'danger', icon: 'ri-close-circle-line' };
        }
        if (produit.stock_actuel <= produit.seuil_alerte) {
            return { label: 'Critique', color: 'warning', icon: 'ri-alert-line' };
        }
        return { label: 'Normal', color: 'success', icon: 'ri-check-line' };
    };

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                </div>
            </React.Fragment>
        );
    }

    if (error || !produit) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger">
                        {error || "Produit non trouvé"}
                    </Alert>
                </div>
            </React.Fragment>
        );
    }

    const stockStatus = getStockStatus();

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <ol className="breadcrumb fs-sm mb-1">
                                        <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                                        <li className="breadcrumb-item"><Link to="/stock">Stock</Link></li>
                                        <li className="breadcrumb-item"><Link to="/stock/produits">Produits</Link></li>
                                        <li className="breadcrumb-item active">Détail produit</li>
                                    </ol>
                                    <h4 className="main-title mb-0">
                                        <i className="ri-eye-line text-primary me-2"></i>
                                        Détail du produit
                                    </h4>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => navigate('/stock/produits')}
                                    >
                                        <i className="ri-arrow-left-line me-2"></i>
                                        Retour
                                    </Button>
                                    <Button 
                                        variant="primary" 
                                        onClick={() => navigate(`/stock/produits/modifier/${id}`)}
                                    >
                                        <i className="ri-pencil-line me-2"></i>
                                        Modifier
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Informations principales */}
                    <Row className="g-4">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Header className="bg-primary text-white">
                                    <h5 className="mb-0">
                                        <i className="ri-information-line me-2"></i>
                                        Informations générales
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-4">
                                        <Col md={6}>
                                            <div className="info-item p-3 bg-light rounded">
                                                <small className="text-muted d-block">Référence</small>
                                                <h5 className="mb-0">{produit.reference}</h5>
                                                {produit.code_barre && (
                                                    <small className="text-muted">Code-barres: {produit.code_barre}</small>
                                                )}
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-item p-3 bg-light rounded">
                                                <small className="text-muted d-block">Désignation</small>
                                                <h5 className="mb-0">{produit.designation}</h5>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-item p-3 bg-light rounded">
                                                <small className="text-muted d-block">Catégorie</small>
                                                <h5 className="mb-0">{produit.nom_categorie || 'Non catégorisé'}</h5>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-item p-3 bg-light rounded">
                                                <small className="text-muted d-block">Unité de mesure</small>
                                                <h5 className="mb-0">{produit.unite_mesure}</h5>
                                            </div>
                                        </Col>
                                        <Col md={12}>
                                            <div className="info-item p-3 bg-light rounded">
                                                <small className="text-muted d-block">Description</small>
                                                <p className="mb-0">{produit.description || 'Aucune description'}</p>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Prix et taxes */}
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Header className="bg-primary text-white">
                                    <h5 className="mb-0">
                                        <i className="ri-price-tag-3-line me-2"></i>
                                        Prix et taxes
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-4">
                                        <Col md={4}>
                                            <div className="info-item p-3 bg-light rounded text-center">
                                                <small className="text-muted d-block">Prix d'achat HT</small>
                                                <h4 className="text-muted mb-0">{formatCurrency(produit.prix_achat_ht)}</h4>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="info-item p-3 bg-light rounded text-center">
                                                <small className="text-muted d-block">Prix de vente HT</small>
                                                <h4 className="text-primary mb-0">{formatCurrency(produit.prix_vente_ht)}</h4>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="info-item p-3 bg-light rounded text-center">
                                                <small className="text-muted d-block">Marge</small>
                                                <h4 className="text-success mb-0">
                                                    {formatCurrency(produit.prix_vente_ht - produit.prix_achat_ht)}
                                                </h4>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="info-item p-3 bg-light rounded">
                                                <small className="text-muted d-block">TVA</small>
                                                <h5 className="mb-0">{produit.tva_taux}%</h5>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="info-item p-3 bg-light rounded">
                                                <small className="text-muted d-block">CSS</small>
                                                <h5 className="mb-0">{produit.css_taux}%</h5>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Colonne droite - Stock */}
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Header className="bg-primary text-white">
                                    <h5 className="mb-0">
                                        <i className="ri-stack-line me-2"></i>
                                        État du stock
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    <div className="text-center mb-4">
                                        <div className={`stock-circle mx-auto mb-3 bg-${stockStatus.color} bg-opacity-10`}>
                                            <h1 className={`display-4 text-${stockStatus.color}`}>
                                                {formatNumber(produit.stock_actuel || 0)}
                                            </h1>
                                        </div>
                                        <Badge bg={stockStatus.color} className="px-4 py-2">
                                            <i className={`${stockStatus.icon} me-2`}></i>
                                            {stockStatus.label}
                                        </Badge>
                                    </div>

                                    <hr />

                                    <Row className="g-3">
                                        <Col xs={6}>
                                            <div className="p-3 bg-light rounded text-center">
                                                <small className="text-muted d-block">Seuil d'alerte</small>
                                                <h5 className="mb-0">{produit.seuil_alerte}</h5>
                                            </div>
                                        </Col>
                                        <Col xs={6}>
                                            <div className="p-3 bg-light rounded text-center">
                                                <small className="text-muted d-block">Stock min</small>
                                                <h5 className="mb-0">{produit.stock_minimum}</h5>
                                            </div>
                                        </Col>
                                        <Col xs={6}>
                                            <div className="p-3 bg-light rounded text-center">
                                                <small className="text-muted d-block">Stock max</small>
                                                <h5 className="mb-0">{produit.stock_maximum || '-'}</h5>
                                            </div>
                                        </Col>
                                        <Col xs={6}>
                                            <div className="p-3 bg-light rounded text-center">
                                                <small className="text-muted d-block">Emplacement</small>
                                                <h5 className="mb-0">{produit.emplacement || '-'}</h5>
                                            </div>
                                        </Col>
                                    </Row>

                                    <div className="d-grid gap-2 mt-4">
                                        <Button 
                                            variant="success" 
                                            onClick={() => navigate(`/stock/mouvements/ajouter/${id}?type=entree`)}
                                        >
                                            <i className="ri-add-box-line me-2"></i>
                                            Ajouter au stock
                                        </Button>
                                        <Button 
                                            variant="warning" 
                                            onClick={() => navigate(`/stock/mouvements/ajouter/${id}?type=sortie`)}
                                            disabled={produit.stock_actuel <= 0}
                                        >
                                            <i className="ri-subtract-line me-2"></i>
                                            Retirer du stock
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Historique des mouvements */}
                        <Col lg={12}>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-primary text-white">
                                    <h5 className="mb-0">
                                        <i className="ri-history-line me-2"></i>
                                        Historique des mouvements
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    <div className="table-responsive">
                                        <Table hover className="align-middle">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Type</th>
                                                    <th className="text-center">Quantité</th>
                                                    <th className="text-center">Stock avant</th>
                                                    <th className="text-center">Stock après</th>
                                                    <th>Document</th>
                                                    <th>Commentaire</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {mouvements.length > 0 ? (
                                                    mouvements.map((mvt, idx) => (
                                                        <tr key={idx}>
                                                            <td>{formatDate(mvt.date_mouvement)}</td>
                                                            <td>
                                                                <Badge bg={
                                                                    mvt.type_mouvement === 'entree' ? 'success' :
                                                                    mvt.type_mouvement === 'sortie' ? 'danger' : 'info'
                                                                }>
                                                                    <i className={`ri-${
                                                                        mvt.type_mouvement === 'entree' ? 'add' :
                                                                        mvt.type_mouvement === 'sortie' ? 'subtract' : 'information'
                                                                    }-line me-1`}></i>
                                                                    {mvt.type_mouvement}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-center fw-bold">
                                                                {mvt.type_mouvement === 'entree' ? '+' : '-'}
                                                                {mvt.quantite}
                                                            </td>
                                                            <td className="text-center">{mvt.stock_avant}</td>
                                                            <td className="text-center">{mvt.stock_apres}</td>
                                                            <td>
                                                                {mvt.reference_document || '-'}
                                                                {mvt.type_document && (
                                                                    <small className="d-block text-muted">{mvt.type_document}</small>
                                                                )}
                                                            </td>
                                                            <td>{mvt.commentaire || '-'}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-4">
                                                            <i className="ri-history-line fs-48 text-muted mb-3 d-block"></i>
                                                            <p className="text-muted">Aucun mouvement enregistré</p>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            <style>{`
                .stock-circle {
                    width: 120px;
                    height: 120px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                }
                .stock-circle h1 {
                    font-size: 2.5rem;
                    font-weight: 700;
                }
                .info-item {
                    transition: all 0.2s ease;
                }
                .info-item:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
            `}</style>
        </React.Fragment>
    );
}